import { PartnerSchema } from '@/lib/types';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { fetchPartners } from '@/lib/api';
import PartnerLogo2 from '@/assets/PartnerLogo2.png';
import { FormattedMessage } from 'react-intl';
import PartnerBadge from '@/components/PartnerBadge/PartnerBadge';

const PartnerHeader = ({
  partnerId,
}: {
  partnerId: string | null;
}) => {
  const { data } = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () =>
      fetchPartners({ partner_ids: [Number(partnerId)] }),
  });
  const partner = data?.find?.(
    (d: PartnerSchema) => d.id === Number(partnerId),
  );

  return (
    <Root>
      {partner?.image ? <PartnerLogo /> : null}
      <PartnerDetails isBig>
        <Label>
          <FormattedMessage
            id="partnerName"
            defaultMessage="Partner Name"
          />
        </Label>
        <PartnerName>{partner?.name}</PartnerName>
      </PartnerDetails>
      <Divider />
      <PartnerDetails>
        <Label>
          <FormattedMessage
            id="partnerType"
            defaultMessage="Partner Type"
          />
        </Label>
        <PartnerBadge type={partner?.type} />
      </PartnerDetails>
      {partner?.platform ? (
        <>
          <Divider />
          <PartnerDetails>
            <Label>
              <FormattedMessage
                id="platform"
                defaultMessage="Platform"
              />
            </Label>
            <PartnerPlatform>{partner?.platform}</PartnerPlatform>
          </PartnerDetails>
        </>
      ) : null}
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(2)} 40px`,
  gap: theme.spacing(2),
}));

const PartnerLogo = styled('div')(({ theme }) => ({
  width: 80,
  height: 80,
  background: theme.palette.grey50,
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.xxs,
  backgroundImage: `url(${PartnerLogo2})`,
  backgroundSize: '69px 16px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
}));

const PartnerDetails = styled('div')<{ isBig?: boolean }>(
  ({ isBig }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 8,
    flexGrow: isBig ? 1 : 0,
    flexBasis: '20%',
  }),
);

const Label = styled('div')(({ theme }) => ({
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.textSecondaryDarkGrey,
}));

const PartnerName = styled('div')({
  fontSize: 14,
  lineHeight: '20px',
});

const PartnerPlatform = styled('div')(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.primary.main,
}));

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  height: '50px',
  background: theme.palette.grey200,
}));

export default PartnerHeader;
